import type { GetToken } from '../../shared/sgapp-subscription';
import { isStampFacts, type StampFacts } from '../core/stamps';

/**
 * The stamp card's I/O in the browser: the read from our own Worker, and which stamps this
 * device has already shown filled. Handed to the component, so its tests fake all of it.
 */
export interface StampsIO {
  /** The visitor's facts, or null for no card: no key (204), signed out, or any failure. */
  fetchFacts: (fresh: boolean) => Promise<StampFacts | null>;
  /**
   * Stamp ids already shown filled on this device, with `first` set when nothing has been
   * recorded yet; null when storage is unavailable.
   */
  readSeen: (userId: string) => { seen: Set<string>; first: boolean } | null;
  writeSeen: (userId: string, seen: Set<string>) => void;
  referrer: () => string;
}

const FETCH_TIMEOUT_MS = 5000;
const seenKey = (userId: string) => `labs-stamps-seen:${userId}`;

export function browserStampsIO(getToken: GetToken | undefined): StampsIO {
  return {
    async fetchFacts(fresh) {
      try {
        const token = await getToken?.();
        if (!token) return null;
        const response = await fetch(`/api/stamps${fresh ? '?fresh=1' : ''}`, {
          headers: { authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
        });
        if (response.status !== 200) return null;
        const body: unknown = await response.json();
        return isStampFacts(body) ? body : null;
      } catch { return null; }
    },
    readSeen(userId) {
      try {
        const raw = window.localStorage.getItem(seenKey(userId));
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        const seen = Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === 'string')) : new Set<string>();
        return { seen, first: raw === null };
      } catch { return null; }
    },
    writeSeen(userId, seen) {
      try { window.localStorage.setItem(seenKey(userId), JSON.stringify([...seen])); } catch { /* A per-device convenience. */ }
    },
    referrer: () => document.referrer,
  };
}
